import { useEffect } from 'react';

import dynamic from 'next/dynamic';
import { useAmp } from 'next/amp';

import { ANALYTICS, POST_TYPE_EBOOK } from '~/utils/Constants';
import { getPostData } from '~/utils/api';
import { createPrefix, toSnakeCase } from '~/utils/Helpers';
import useAnalytics from '~/services/AnalyticsService';

const { EVENT_NAME, SCREEN_NAMES } = ANALYTICS;

export const config = { amp: 'hybrid' };

const Single = dynamic(() => import('~/pageComponents/Single/Single'));
const Ebook = dynamic(() => import('~/pageComponents/Ebook/Ebook'));
const SingleAMP = dynamic(
  () => import('~/pageComponents/Single/amp/SingleAMP'),
);

export default function SinglePage({ postData }) {
  const { sendEvent } = useAnalytics();
  const isAmp = useAmp();

  useEffect(() => {
    sendEvent(EVENT_NAME.pageOpen, {
      screen_name: SCREEN_NAMES.blog,
      page_type: postData.post_type,
      page_name: toSnakeCase(postData.slug),
      post_content_type: postData.content_type,
    });
  }, [sendEvent, postData.slug, postData.content_type, postData.post_type]);

  let singleComponent = <Single postData={postData} />;
  if (isAmp) {
    singleComponent = <SingleAMP postData={postData} />;
  } else if (postData.post_type === POST_TYPE_EBOOK) {
    singleComponent = <Ebook ebookData={postData} />;
  }

  return <>{singleComponent}</>;
}

export async function getServerSideProps({ params, query, req, res }) {
  const slug = encodeURIComponent(params.slug);
  const pathPrefix = createPrefix(req);

  const [postData] = await Promise.all([
    getPostData(slug, !!query.amp, pathPrefix),
  ]);

  const [postDataToReturn] = postData;
  if (postDataToReturn?.post_redirect !== null) {
    const status = parseInt(postDataToReturn?.post_redirect_status, 10);
    if (postDataToReturn) {
      return res.redirect(status, postDataToReturn?.post_redirect);
    }
    return { notFound: true };
  }
  if (!postDataToReturn) {
    return { notFound: true };
  }

  if (postDataToReturn.is_old_slug) {
    return {
      redirect: {
        destination: `${pathPrefix}/${postDataToReturn.slug}`,
        statusCode: 301,
      },
    };
  }

  return {
    props: {
      postData: postDataToReturn,
    },
  };
}
